import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import type { ContentSummary } from '@cms/contracts'
import { ApiError } from '../api/client.js'
import { listContents } from '../api/endpoints.js'
import { displayName, useCan, useSession } from '../auth/session.js'
import { absoluteDateTime, relativeTime } from '../domain/format.js'
import { displayDomain, liveUrl } from '../domain/live-url.js'
import { groupSites } from '../domain/site-groups.js'
import { useI18n } from '../i18n/context.js'
import { paths } from '../routes.js'
import { useSiteWorkspace } from '../workspace/site.js'
import { siteStatusTone } from '../shell/SiteSwitcher.js'
import { Banner, Card, EmptyState, Icon, IconExternal, LinkButton, PageHeader, StatusChip, TableSkeleton } from '../ui/primitives.js'
import { contentStatusTone, contentTypeIcon } from '../ui/status.js'

/**
 * The home page: what you worked on lately, across every site you can see,
 * and the sites themselves grouped by client project. `GET /contents`
 * without a site answers across all the sites the person can read.
 */
export function HomeScreen() {
  const { t, locale } = useI18n()
  const { state } = useSession()
  const { sites, projectNames, tenant, loading: sitesLoading, error: sitesError } = useSiteWorkspace()
  const canProvision = useCan('cms:provision')
  const [recent, setRecent] = useState<ContentSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const now = Date.now()
  const person = state.status === 'signed-in' ? state.person : null
  const groups = useMemo(() => groupSites(sites, projectNames, '', t('switcher.otherProject')), [sites, projectNames, t])

  useEffect(() => {
    let cancelled = false
    listContents({ limit: 10 })
      .then((page) => { if (!cancelled) setRecent([...page.items].sort((a, b) => b.updatedAt - a.updatedAt)) })
      .catch((err: unknown) => { if (!cancelled) setError(err instanceof ApiError ? err.message : t('errors.network')) })
    return () => { cancelled = true }
  }, [t])

  const siteName = (id: string) => sites.find((site) => site.id === id)?.name ?? ''

  return <div className="page">
    <PageHeader
      eyebrow={tenant?.name}
      title={person ? t('home.greeting', { name: (displayName(person).split(/[\s@]/)[0] ?? '') }) : t('nav.home')}
      subtitle={t('home.subtitle')}
      actions={canProvision ? <LinkButton variant="outlined" icon="add_business" to={paths.newSite()}>{t('provision.short')}</LinkButton> : undefined}
    />

    <Card title={t('home.recent')} subtitle={t('home.recentHelp')} icon="history">
      {error ? <Banner tone="error" title={t('list.errorTitle')}>{error}</Banner>
        : recent === null ? <TableSkeleton rows={5} columns={4} label={t('common.loading')}/>
        : recent.length === 0 ? <EmptyState compact icon="article" title={t('home.noRecent')}/>
        : <ul className="list">
          {recent.map((item) => <li key={item.id}>
            <Link className="list-row" to={paths.content(item.siteId, item.id)}>
              <span className="list-row-icon"><Icon name={contentTypeIcon(item.contentTypeKey)}/></span>
              <span className="list-row-text">
                <strong>{item.title || item.slug}</strong>
                <small>{[siteName(item.siteId), item.locale.toUpperCase()].filter(Boolean).join(' · ')}</small>
              </span>
              <StatusChip tone={contentStatusTone(item.status)}>{t(`status.${item.status}`)}</StatusChip>
              <time className="list-row-meta" dateTime={new Date(item.updatedAt).toISOString()} title={absoluteDateTime(item.updatedAt, locale)}>{relativeTime(item.updatedAt, now, locale)}</time>
            </Link>
          </li>)}
        </ul>}
    </Card>

    <section className="page-section" aria-labelledby="home-sites">
      <div className="section-row"><h2 className="section-title" id="home-sites">{t('home.sites')}</h2></div>
      {sitesError && <Banner tone="error">{sitesError}</Banner>}
      {sitesLoading && !sites.length ? <TableSkeleton rows={3} columns={3} label={t('common.loading')}/>
        : !sites.length ? <EmptyState icon="domain" title={t('dashboard.noSite')} action={canProvision ? <LinkButton variant="filled" icon="add_business" to={paths.newSite()}>{t('provision.short')}</LinkButton> : undefined}>
          <p>{t('home.noSitesHelp')}</p>
        </EmptyState>
        : groups.map((group) => <div key={group.projectId} className="site-group">
          <h3 className="site-group-title"><Icon name="apartment" size={20}/>{group.projectName}</h3>
          <div className="card-grid">
            {group.sites.map((site) => {
              const url = liveUrl(site.primaryDomain)
              return <article className="card site-card" key={site.id}>
                <Link to={paths.site(site.id)} className="site-card-main">
                  <span className="site-card-icon"><Icon name="public"/></span>
                  <span className="site-card-text"><strong>{site.name}</strong><small>{displayDomain(site.primaryDomain)}</small></span>
                </Link>
                <div className="site-card-foot">
                  <StatusChip tone={siteStatusTone(site.status)}>{t(`siteStatus.${site.status}`)}</StatusChip>
                  <span className="muted">{site.lastPublishedAt ? t('site.lastPublished', { when: relativeTime(site.lastPublishedAt, now, locale) }) : t('site.neverPublished')}</span>
                  {url && <IconExternal href={url} label={t('live.openSite')}/>}
                </div>
              </article>
            })}
          </div>
        </div>)}
    </section>
  </div>
}
